/// <amd-module name="Wit/BoardsHub/KanbanBoard/AnnotationService" />
import { ITeam } from "Agile/Contracts/Common";
import * as React from "react";
import { SuiteEntryTypes, SuiteEntryUpdateParams, TestPointUpdateParams } from "TestPlanCommon/Contracts/TestPlan";
import { ITestPlanRestClient, TestPlanClientName } from "TestPlanCommon/RestClient/TestPlan";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { BoardAnnotationsIdentifiers } from "Wit/BoardsHub/Common/AnnotationUtils";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import {
    IChecklistAnnotationInfo,
    IChecklistAnnotationTypeInfo,
    IChecklistItem,
    IGitHubAnnotationInfo,
    IItemAnnotations,
    IObservableBoardsWorkItem,
    ITestAnnotationInfo,
    ITestItem,
    TestOutcome
} from "Wit/BoardsHub/Common/Interfaces";
import { IAnnotationSetting } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import {
    ExternalArtifactType,
    ExternalLinkedArtifactIdentifier,
    GitHubLinkItemsRequest,
    GitHubLinkItemsResponse,
    GitHubLinksAnnotationItemSourceDataProviderRequest,
    GitHubLinksAnnotationItemSourceDataProviderResponse
} from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { WorkItemLink } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { BacklogConfiguration } from "Wit/Clients/Work/Contracts/Work";
import { FieldRefNames } from "Wit/ClientWorkItemService/IClientWorkItem";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { getBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { LinkTypeName } from "Wit/WorkItemModel/OM/WorkItemConstants";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import * as WorkItemModelLink from "Wit/WorkItemModel/WorkItem/Links";
import { IAnnotationService } from "./IAnnotationService";
import {
    compareTestItems,
    getSuiteForWorkItem,
    getTestAnnotationData,
    getTestPointUpdate,
    IWitTestsForKanbanBoard,
    testOutcomeFromString,
    updateTestCounts
} from "./Utils/TestAnnotationUtils";

const checklistAnnotationRegex = /^Microsoft.VSTS.Agile.(\S+)Annotation$/;
const testedByLinkNameReverse = "Microsoft.VSTS.Common.TestedBy-Reverse";
const testedByLinkName = "Microsoft.VSTS.Common.TestedBy";

export function isChecklistAnnotation(id: string) {
    return checklistAnnotationRegex.test(id);
}

enum WorkItemStateCategory {
    Proposed = 1000,
    InProgress = 2000,
    Resolved = 3000,
    Completed = 4000,
    Removed = 5000
}

class AnnotationService extends VssService implements IAnnotationService {
    private _checklistAnnotations: Map<number, IChecklistAnnotationInfo>;
    private _team: ITeam | undefined;
    private _testAnnotations?: Map<number, ITestAnnotationInfo>;
    private _workItemTypeMappedStates: Map<
        string,
        {
            [state: string]: WorkItemStateCategory;
        }
    >;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const backlogConfiguration = contributionService.getData<BacklogConfiguration>("ms.vss-work-web.agile-backlog-configuration-data-provider");
        this._team = contributionService.getData<ITeam | undefined>("ms.vss-tfs-web.team-context-data-provider");
        this._workItemTypeMappedStates = new Map(
            backlogConfiguration?.workItemTypeMappedStates.map(witms => [
                witms.workItemTypeName,
                witms.states as unknown as { [state: string]: WorkItemStateCategory }
            ])
        );
    }

    public addChecklistAnnotation(parent: IObservableBoardsWorkItem, checklistItem: IChecklistItem): void {
        const annotationInfo = this.getChecklistItems(parent.value.id, checklistItem.type);
        if (annotationInfo) {
            annotationInfo.items.push(checklistItem);
            this._updateAnnotationsForWorkItem(parent);
        }
    }

    public async addTestAnnotation(parent: IObservableBoardsWorkItem, testItem: ITestItem): Promise<void> {
        const annotationInfo = await this.getTestItems(parent.value.id);

        await this.setTestPointId(parent.value.id, testItem);

        if (annotationInfo) {
            annotationInfo.tests.push(testItem);
            this._updateAnnotationsForWorkItem(parent);
        }
    }

    private async setTestPointId(parentWorkItemId: number, testItem: ITestItem): Promise<void> {
        const teamId = this._team?.id ?? "";
        const testData = await getTestAnnotationData(this.pageContext, teamId, [parentWorkItemId]);

        const annotation = testData?.find(td => td.requirementId === parentWorkItemId);

        if (!annotation) {
            return;
        }

        const test = annotation.testPoints.find(testPoint => testPoint.testCaseId === testItem.id);
        if (test) {
            testItem.testPointId = test.testPointId;
        }
    }

    public removeChecklistAnnotation(parent: IObservableBoardsWorkItem, checklistItem: IChecklistItem): void {
        const items = this.getChecklistItems(parent.value.id, checklistItem.type)?.items;
        if (!items) {
            return;
        }

        const checklistItemIndex = items.value.findIndex(item => checklistItem.id === item.id);
        if (checklistItemIndex > -1) {
            items.splice(checklistItemIndex, 1);
        }
        this._updateAnnotationsForWorkItem(parent);
    }

    public async removeTestAnnotation(projectId: string, parent: IObservableBoardsWorkItem, testItem: ITestItem, deleted?: boolean): Promise<void> {
        const items = await this.getTestItems(parent.value.id);
        if (!items) {
            return;
        }

        const testList = items.tests;
        const testItemIndex = testList.value.findIndex(item => testItem.id === item.id);
        if (testItemIndex > -1) {
            testList.splice(testItemIndex, 1);
        }

        if (deleted) {
            // if deleted, still need to update parent card but don't need to delete again
            this._updateAnnotationsForWorkItem(parent);
            return;
        }
        const testClient = this.pageContext.getRestClient<ITestPlanRestClient>(TestPlanClientName);
        try {
            await testClient.removeTestCasesListFromSuite(projectId, items.planId, items.suiteId, testItem.id.toString());
            this._updateAnnotationsForWorkItem(parent);
        } catch (e) {
            // undo the change and show an error
            testList.splice(testItemIndex, 0, testItem);
            this._showAnnotationError(e);
        }
    }

    public async moveChecklistAnnotation(
        projectId: string,
        teamId: string,
        parentId: number,
        checklistItem: IChecklistItem,
        newIndex?: number,
        previousParentId?: number,
        previousIndex?: number,
        nextIndex?: number,
        onComplete?: () => void
    ): Promise<void> {
        const items = this.getChecklistItems(parentId, checklistItem.type)?.items;
        const previousItems = this.getChecklistItems(previousParentId, checklistItem.type)?.items;
        if (!items) {
            return;
        }

        const previousId = previousIndex !== undefined ? items.value[previousIndex].id : undefined;
        const nextId = nextIndex !== undefined ? items.value[nextIndex].id : undefined;
        const currentIndex = (previousItems ?? items).value.findIndex(item => item.id === checklistItem.id);

        // place at the end of the target list if a new index wasn't provided
        newIndex = newIndex ?? items.length;
        moveItemInLists(items, currentIndex, checklistItem, newIndex, previousItems);
        const processError = (error: any) => {
            moveItemInLists(previousItems ?? items, newIndex!, checklistItem, currentIndex, items);
            this._showAnnotationError(error);
        };

        const workItemService = this.pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
        try {
            const parent = workItemService.get(parentId);
            this._updateAnnotationsForWorkItem(parent);
            if (previousParentId && previousParentId !== parentId) {
                const previousParent = workItemService.get(previousParentId);
                this._updateAnnotationsForWorkItem(previousParent);
                await workItemService.reparentWorkItems([checklistItem.id], parentId);
            }
        } catch (e) {
            processError(e);
        }

        if (previousId || nextId) {
            workItemService.queueReorderOperation({
                ids: [checklistItem.id],
                projectId,
                teamId,
                nextId: nextId ?? 0,
                previousId: previousId ?? 0,
                parentId: parentId,
                onFailure: error => {
                    // undo the move
                    onComplete?.();
                    processError(error);
                },
                onSuccess: reorderResult => {
                    for (const result of reorderResult) {
                        const item = items!.value.find(item => item.id === result.id);
                        if (item) {
                            item.order = result.order;
                        }
                    }

                    onComplete?.();
                }
            });
        } else {
            onComplete?.();
        }
    }

    public async moveTestAnnotation(
        projectId: string,
        parentId: number,
        testItem: ITestItem,
        newIndex?: number,
        previousParentId?: number
    ): Promise<void> {
        const testAnnotation = await this.getTestItems(parentId);
        const items = testAnnotation?.tests;
        const previousItems = (await this.getTestItems(previousParentId))?.tests;
        if (!items) {
            return;
        }

        // place at the end of the target list if a new index wasn't provided
        const currentIndex = (previousItems ?? items).value.findIndex(item => item.id === testItem.id);
        newIndex = newIndex ?? items.length;
        moveItemInLists(items, currentIndex, testItem, newIndex, previousItems);

        try {
            const workItemService = this.pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
            const parent = workItemService.get(parentId);
            this._updateAnnotationsForWorkItem(parent);
            if (previousParentId && previousParentId !== parentId) {
                const previousParent = workItemService.get(previousParentId);
                this._updateAnnotationsForWorkItem(previousParent);
                await this._reparentTestCase(testItem.id, parentId);
            }

            if (newIndex !== undefined && testAnnotation.suiteId > 0) {
                await this._updateTestCaseOrder(projectId, testAnnotation.suiteId, items.value);
            }
        } catch (e) {
            // undo the move
            moveItemInLists(previousItems ?? items, newIndex, testItem, currentIndex, items);
            this._showAnnotationError(e);
        }
    }

    private getChecklistItems(parentId?: number, type?: string): IChecklistAnnotationTypeInfo | undefined {
        if (!parentId || !type) {
            return;
        }

        let annotationInfo = this._checklistAnnotations.get(parentId);
        if (!annotationInfo) {
            annotationInfo = {};
            this._checklistAnnotations.set(parentId, annotationInfo);
        }

        let annotationTypeInfo = annotationInfo[type];
        if (!annotationTypeInfo) {
            annotationTypeInfo = { items: new ObservableArray([]) };
            annotationInfo[type] = annotationTypeInfo;
        }

        return annotationTypeInfo;
    }

    private async getTestItems(parentId?: number) {
        if (!parentId || !this._testAnnotations) {
            return;
        }

        let annotationInfo = this._testAnnotations.get(parentId);
        if (!annotationInfo) {
            // initialize TCM suite and plan if this requirement doesn't have them yet
            annotationInfo = await this._createTestDataForRequirement(parentId);
            this._testAnnotations.set(parentId, annotationInfo);
        }

        return annotationInfo;
    }

    private async _createTestDataForRequirement(parentId: number): Promise<ITestAnnotationInfo> {
        const teamId = this._team?.id ?? "";
        const suiteData = await getSuiteForWorkItem(this.pageContext, teamId, parentId);
        return {
            failed: 0,
            notApplicable: 0,
            other: 0,
            passed: 0,
            suiteId: suiteData.testSuiteId,
            planId: suiteData.testPlanId,
            tests: new ObservableArray([])
        };
    }

    private _showAnnotationError(e: any) {
        const messageService = getBoardsMessagesService(this.pageContext);
        messageService.showMessage({
            renderMessage: () => <span>{format(Resources.FailedToUpdateAnnotationErrorMessage, e?.message)}</span>,
            severity: MessageCardSeverity.Error
        });
    }

    private _updateAnnotationsForWorkItem(workItem?: IObservableBoardsWorkItem) {
        if (!workItem) {
            return;
        }

        const checklist = this._checklistAnnotations.get(workItem.value.id);
        if (!workItem.value.annotations) {
            workItem.value.annotations = { checklist };
        } else {
            workItem.value.annotations.checklist = checklist;
        }

        if (this._testAnnotations) {
            const tests = this._testAnnotations.get(workItem.value.id);
            updateTestCounts(tests);
            if (!workItem.value.annotations) {
                workItem.value.annotations = { tests };
            } else {
                workItem.value.annotations.tests = tests;
            }
        }

        workItem.notify();
    }

    public async setItemCompletion(parent: IObservableBoardsWorkItem, itemId: number, completed: boolean) {
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const item = await workItemModelService.getWorkItem(itemId);
        const type = item.getFieldValueByName(CoreFieldRefNames.WorkItemType) as string;
        let targetCategory = completed ? WorkItemStateCategory.Completed : WorkItemStateCategory.Proposed;
        this._setItemStateFromCategory(item, targetCategory);
        if (item.infoText.value.invalid && targetCategory === WorkItemStateCategory.Proposed) {
            // if we can't transition to Proposed, try InProgress
            targetCategory = WorkItemStateCategory.InProgress;
            this._setItemStateFromCategory(item, targetCategory);
        }
        if (item.infoText.value.invalid) {
            throw new Error(Resources.ItemCompletionInvalid);
        }
        await item.save();

        const annotation = parent.value.annotations?.checklist?.[type];
        const annotationItem = annotation?.items.value.find(i => i.id === itemId);
        if (annotationItem) {
            annotationItem.completed = completed;
        }
        parent.notify();
    }

    public async setTestOutcome(projectId: string, parent: IObservableBoardsWorkItem, testItem: ITestItem, outcome: TestOutcome): Promise<void> {
        const parentTestInfo = await this.getTestItems(parent.value.id);
        if (!parentTestInfo) {
            return;
        }

        const originalOutcome = testItem.outcome;
        testItem.outcome = outcome;
        updateTestCounts(parentTestInfo);

        // List component caches aggressively, so this is required to make sure it re-renders after the change
        notifyObservers(parentTestInfo.tests);
        parent.notify();

        const testClient = this.pageContext.getRestClient<ITestPlanRestClient>(TestPlanClientName);
        try {
            const updatedTestPoints: TestPointUpdateParams[] = [getTestPointUpdate(testItem, outcome)];
            await testClient.updateTestPoints(updatedTestPoints, projectId, parentTestInfo.planId, parentTestInfo.suiteId);
        } catch (e) {
            testItem.outcome = originalOutcome;
            updateTestCounts(parentTestInfo);
            notifyObservers(parentTestInfo.tests);
            parent.notify();
            this._showAnnotationError(e);
        }
    }

    private _setItemStateFromCategory(item: IWorkItem, targetCategory: WorkItemStateCategory) {
        const type = item.getFieldValueByName(CoreFieldRefNames.WorkItemType) as string;
        const targetState = this._getWorkItemStateForCategory(type, targetCategory);
        if (!targetState) {
            throw new Error(Resources.NoTargetState);
        }
        item.setFieldValueByName(CoreFieldRefNames.State, targetState);
    }

    public async getAnnotationsForItemsAsync(
        itemIds: number[],
        teamId: string,
        annotationSettings: IAnnotationSetting[],
        orderFieldName: string = FieldRefNames.StackRank
    ): Promise<void> {
        if (annotationSettings.length === 0 || itemIds.length === 0) {
            return;
        }

        let gitHubAnnotationsPromise: Promise<Map<number, IGitHubAnnotationInfo>> | undefined;
        const checklistAnnotationTypes: string[] = [];
        let testAnnotationsPromise: Promise<Map<number, ITestAnnotationInfo>> | undefined;

        for (const a of annotationSettings) {
            if (!a.isEnabled) {
                continue;
            }

            if (isChecklistAnnotation(a.id)) {
                checklistAnnotationTypes.push(a.name);
            } else if (a.id === BoardAnnotationsIdentifiers.GitHubLinksAnnotation) {
                gitHubAnnotationsPromise = this._getGitHubAnnotations(itemIds);
            } else if (a.id === BoardAnnotationsIdentifiers.TestAnnotation) {
                testAnnotationsPromise = this._getTestAnnotations(teamId, itemIds);
            } else {
                console.error(`Unsupported annotation id ${a.id} %O`, a);
            }
        }

        const [checklistAnnotations, gitHubAnnotations, testAnnotations] = await Promise.all([
            this._getChecklistAnnotations(itemIds, checklistAnnotationTypes, orderFieldName),
            gitHubAnnotationsPromise,
            testAnnotationsPromise
        ]);

        this._checklistAnnotations = checklistAnnotations;
        this._testAnnotations = testAnnotations;

        const workItemService = this.pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");

        for (const id of itemIds) {
            const annotations: IItemAnnotations = {};
            if (checklistAnnotations.has(id)) {
                annotations.checklist = checklistAnnotations.get(id);
            }
            if (gitHubAnnotations?.has(id)) {
                annotations.gitHub = gitHubAnnotations.get(id);
            }
            if (testAnnotations?.has(id)) {
                annotations.tests = testAnnotations.get(id);
            }
            const workItem = workItemService.get(id);
            if (workItem) {
                if (annotations.gitHub || annotations.checklist || annotations.tests) {
                    workItem.value.annotations = annotations;
                    workItem.notify();
                } else if (workItem.value.annotations) {
                    workItem.value.annotations = undefined;
                    workItem.notify();
                }
            }
        }
    }

    private async _getChecklistAnnotations(
        itemIds: number[],
        types: string[],
        orderFieldRefName: string
    ): Promise<Map<number, IChecklistAnnotationInfo>> {
        const workItemService = this.pageContext.getService<IWorkItemService>("work-item-service");
        const map = new Map<number, IChecklistAnnotationInfo>();

        if (itemIds.length === 0 || types.length === 0) {
            return map;
        }

        let workItemRelations: WorkItemLink[];
        let childItems: Map<
            number,
            {
                [key: string]: any;
            }
        >;

        try {
            // get annotation info for each item
            const witClient = getWorkItemTrackingClient(this.pageContext);
            const project = getTFSProject(this.pageContext)!;
            const query = `SELECT [${CoreFieldRefNames.Id}] FROM WorkItemLinks
            WHERE ([Source].[${CoreFieldRefNames.Id}] IN (${itemIds.toString()})) AND ([System.Links.LinkType] = '${LinkTypeName.Child_Link_Name}')
            AND ([Target].[${CoreFieldRefNames.WorkItemType}] IN (${types.map(t => `'${t}'`)})) mode(MustContain)`;
            workItemRelations = (await witClient.queryByWiql({ query }, project.id)).workItemRelations;

            const fields: string[] = [
                CoreFieldRefNames.Id,
                CoreFieldRefNames.Title,
                CoreFieldRefNames.State,
                orderFieldRefName,
                CoreFieldRefNames.WorkItemType,
                CoreFieldRefNames.AssignedTo,
            ];

            // get type, title, and state of all child items
            const childIds = workItemRelations.map(wir => wir.target?.id).filter(id => !!id);
            const childItemList = (
                await Promise.all(
                    workItemService.loadWorkItems(project.id, childIds, fields)
                )
            ).flat();
            childItems = new Map(childItemList.map(item => [item.id, item.fields]));
        } catch (error) {
            console.error(`Error getting checklist annotations: `, error);
            return map;
        }

        // build info for each item
        for (const r of workItemRelations) {
            if (r.source && r.target) {
                const targetItem = childItems.get(r.target.id);
                if (targetItem) {
                    const type = targetItem[CoreFieldRefNames.WorkItemType];
                    const state = targetItem[CoreFieldRefNames.State];
                    const assignedTo = targetItem[CoreFieldRefNames.AssignedTo];
                    const completed = this.isInCompletedState(type, state);
                    let annotationInfo = map.get(r.source.id);
                    if (!annotationInfo) {
                        annotationInfo = {};
                        map.set(r.source.id, annotationInfo);
                    }
                    let annotationTypeInfo = annotationInfo[type];
                    if (!annotationTypeInfo) {
                        annotationTypeInfo = { items: new ObservableArray([]) };
                        annotationInfo[type] = annotationTypeInfo;
                    }
                    annotationTypeInfo.items.push({
                        completed,
                        id: targetItem[CoreFieldRefNames.Id],
                        order: targetItem[orderFieldRefName],
                        saving: new ObservableValue(false),
                        state,
                        title: targetItem[CoreFieldRefNames.Title],
                        type,
                        assignedTo
                    });
                }
            }
        }

        this._sortChecklistAnnotations(map);

        return map;
    }

    private _sortChecklistAnnotations(map: Map<number, IChecklistAnnotationInfo>) {
        for (const [number, annotationInfo] of map) {
            for (const typeAnnotations of Object.values(annotationInfo)) {
                typeAnnotations.items.value = typeAnnotations.items.value.sort(compareChecklistItems);
            }
        }
    }

    private _sortTestAnnotations(map: Map<number, ITestAnnotationInfo>) {
        for (const [number, annotationInfo] of map) {
            annotationInfo.tests.value = annotationInfo.tests.value.sort(compareTestItems);
        }
    }

    private async _updateTestCaseOrder(projectId: string, suiteId: number, testCases: ITestItem[]): Promise<void> {
        const testClient = this.pageContext.getRestClient<ITestPlanRestClient>(TestPlanClientName);
        const testCaseUpdates: SuiteEntryUpdateParams[] = testCases.map((testCase, index) => {
            return { id: testCase.id, sequenceNumber: index + 1, suiteEntryType: SuiteEntryTypes.TestCase };
        });

        await testClient.reorderSuiteEntries(testCaseUpdates, projectId, suiteId);
    }

    private async _reparentTestCase(testCaseId: number, newParentId: number): Promise<void> {
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const linksService = this.pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
        const workItemPromise = workItemModelService.getWorkItem(testCaseId);
        const linkTypePromise = linksService.getWitLinkTypes();
        const [workItem, linkTypes] = await Promise.all([workItemPromise, linkTypePromise]);
        const linkType = linkTypes.find(lt => lt.referenceName === testedByLinkName)?.reverseEnd.id;

        const existingParentLink = linkType !== undefined && workItem.getLinks().find(link => link.linkData.LinkType === linkType);
        if (existingParentLink) {
            workItem.removeLinks([existingParentLink]);
        }

        workItem.addLinks([await WorkItemModelLink.WorkItemLink.create(this.pageContext, testedByLinkNameReverse, newParentId)]);

        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        await savingService.saveWorkItemsBatchAsync([workItem]);
    }

    public isInCompletedState(type: string, state: string) {
        return this._workItemTypeMappedStates.get(type)?.[state] === WorkItemStateCategory.Completed;
    }

    private _getWorkItemStateForCategory(type: string, category: WorkItemStateCategory): string | undefined {
        const map = this._workItemTypeMappedStates.get(type);
        if (!map) {
            return;
        }
        for (const [state, c] of Object.entries(map)) {
            if (c === category) {
                return state;
            }
        }
    }

    private async _getGitHubAnnotations(itemIds: number[]): Promise<Map<number, IGitHubAnnotationInfo>> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<GitHubLinksAnnotationItemSourceDataProviderResponse>(
            "ms.vss-work-web.github-links-annotation-item-source-data-provider",
            { workItemIds: itemIds } as GitHubLinksAnnotationItemSourceDataProviderRequest
        );
        const map = new Map<number, IGitHubAnnotationInfo>();
        const links = data?.links;
        if (!links) {
            return map;
        }
        for (const id of itemIds) {
            if (links[id] && links[id].length > 0) {
                const info: IGitHubAnnotationInfo = {
                    commits: 0,
                    issues: 0,
                    pullRequests: 0,
                    links: links[id]
                };
                for (const l of links[id]) {
                    switch (l.itemType) {
                        case ExternalArtifactType.Commit:
                            info.commits++;
                            break;
                        case ExternalArtifactType.Issue:
                            info.issues++;
                            break;
                        case ExternalArtifactType.PullRequest:
                            info.pullRequests++;
                            break;
                    }
                }
                map.set(id, info);
            }
        }
        return map;
    }

    public async getGitHubAnnotationDetails(itemId: number, links: ExternalLinkedArtifactIdentifier[]) {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<GitHubLinkItemsResponse>("ms.vss-work-web.github-link-data-provider", {
            workItemId: itemId,
            identifiers: links
        } as Partial<GitHubLinkItemsRequest>);
        return data?.resolvedLinkItems;
    }

    private async _getTestAnnotations(teamId: string, itemIds: number[]): Promise<Map<number, ITestAnnotationInfo>> {
        const map = new Map<number, ITestAnnotationInfo>();
        let testData: IWitTestsForKanbanBoard[];
        try {
            testData = await getTestAnnotationData(this.pageContext, teamId, itemIds);
        } catch (error) {
            console.error(`Error getting test annotations: `, error);
            return map;
        }

        for (const test of testData) {
            const parentId = test.requirementId;
            let annotationInfo = map.get(parentId);
            if (!annotationInfo) {
                annotationInfo = {
                    failed: 0,
                    notApplicable: 0,
                    other: 0,
                    passed: 0,
                    planId: test.testPlanId,
                    suiteId: test.testSuiteId,
                    tests: new ObservableArray([])
                };
                map.set(parentId, annotationInfo);
            }
            for (const tp of test.testPoints) {
                const outcome = testOutcomeFromString(tp.outcome);
                annotationInfo.tests.push({
                    id: tp.testCaseId,
                    outcome,
                    title: tp.testCaseTitle,
                    sequenceNumber: tp.sequenceNumber,
                    testPointId: tp.testPointId
                });
            }

            updateTestCounts(annotationInfo);
        }

        this._sortTestAnnotations(map);

        return map;
    }
}

function compareChecklistItems(a: IChecklistItem, b: IChecklistItem) {
    return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
}

function moveItemInLists<T>(
    target: IObservableArray<T>,
    currentIndex: number,
    item: T,
    newIndex: number,
    source?: IObservableArray<T>
): IObservableArray<T> {
    if (!source && newIndex === currentIndex) {
        return target;
    }

    if (!source) {
        source = target;
    }

    // add in the new item
    target.splice(newIndex, 0, item);

    // delete the original item
    if (currentIndex > -1 && currentIndex < source.length) {
        // account for the new item in the list if we're just reordering a single list
        const indexToDelete = source === target && currentIndex > newIndex ? currentIndex + 1 : currentIndex;
        source.splice(indexToDelete, 1);
    }

    return target;
}

function notifyObservers<T>(observableValue: IObservableValue<T> | IObservableArray<T>) {
    // trigger a re-render in any subscribers
    observableValue.value = observableValue.value;
}

Services.add("IAnnotationService", { serviceFactory: AnnotationService });
